/**
 * Canonical client ticket-status model — Sprint 7 WI-1.
 *
 * Server authority is `functions/challenge_ticket_constants.py`
 * (`TICKET_STATUS_{OPEN,ACCEPTED,EXPIRED,CANCELED}`). The client has historically
 * reasoned about ticket status with bare literal strings duplicated across ≥7
 * files (audit T1) and re-derived expiry inconsistently (audit F7).
 *
 * This module mirrors the Sprint 6 `challengePhase.js` pattern: one small status
 * vocabulary every consumer imports, plus a single effective-vs-stored helper so
 * `preview.status` is never trusted raw where effective status is required. It is
 * pure, additive, and side-effect free — no Firestore, no UI, no mutation.
 */

/**
 * Canonical ticket statuses — mirror of the server constants. Frozen so a
 * consumer cannot mutate the vocabulary.
 */
export const TicketStatus = Object.freeze({
  OPEN: 'OPEN',
  ACCEPTED: 'ACCEPTED',
  EXPIRED: 'EXPIRED',
  CANCELED: 'CANCELED',
  DECLINED: 'DECLINED',
});

const KNOWN_STATUSES = new Set(Object.values(TicketStatus));

/**
 * Statuses in which a ticket is no longer live. `OPEN` is the only non-terminal
 * status; everything else is a terminal/inactive end state.
 */
const TERMINAL_STATUSES = new Set([
  TicketStatus.ACCEPTED,
  TicketStatus.EXPIRED,
  TicketStatus.CANCELED,
  TicketStatus.DECLINED,
]);

/**
 * Presentation-layer label per status. Display only — never a persisted value.
 * This is where any spelling normalization lives (audit F2/T3): the server emits
 * `CANCELED` (one L); the UK spelling `CANCELLED` is accepted on input and
 * normalized to the single canonical label so no consumer renders both.
 */
const STATUS_LABEL = {
  [TicketStatus.OPEN]: 'Open',
  [TicketStatus.ACCEPTED]: 'Accepted',
  [TicketStatus.EXPIRED]: 'Expired',
  [TicketStatus.CANCELED]: 'Canceled',
  [TicketStatus.DECLINED]: 'Passed',
};

/**
 * Normalize a raw status value to a canonical `TicketStatus`.
 *
 * Upper-cases, and folds the UK spelling `CANCELLED` → `CANCELED` so display
 * never diverges. Unknown / illegal / empty values return `null` so callers can
 * handle them safely instead of mis-bucketing (the client never invents status).
 *
 * @param {unknown} raw
 * @returns {string|null} a `TicketStatus` value, or null when unrecognized
 */
export function normalizeTicketStatus(raw) {
  const upper = String(raw ?? '').trim().toUpperCase();
  if (!upper) return null;
  if (upper === 'CANCELLED') return TicketStatus.CANCELED;
  return KNOWN_STATUSES.has(upper) ? upper : null;
}

function toMillis(val) {
  if (val == null) return null;
  if (typeof val === 'number') return val;
  if (val?.toMillis) return val.toMillis();
  const t = new Date(val).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Resolve a ticket's effective status — the client mirror of the server
 * `_effective_ticket_status`. A stored `OPEN` ticket whose `expiresAt` has passed
 * is effectively `EXPIRED` even before the 30-minute scheduler persists it
 * (audit F7). Any other stored status is returned as-is.
 *
 * Use this anywhere effective status is required (e.g. raw `preview.status`),
 * rather than reading `ticket.status` directly.
 *
 * @param {object|null|undefined} ticket — expects `status`, optional `expiresAt`
 * @param {{ now?: number|Date }} [opts]
 * @returns {string|null} a `TicketStatus` value, or null when unrecognized
 */
export function effectiveTicketStatus(ticket, { now } = {}) {
  if (!ticket || typeof ticket !== 'object') return null;
  const status = normalizeTicketStatus(ticket.status);
  if (status !== TicketStatus.OPEN) return status;

  const expiresMs = toMillis(ticket.expiresAt);
  if (expiresMs == null) return TicketStatus.OPEN;
  const nowMs = now == null ? Date.now() : toMillis(now);
  return nowMs != null && nowMs >= expiresMs ? TicketStatus.EXPIRED : TicketStatus.OPEN;
}

/** @param {string|null} status @returns {boolean} terminal (accepted/expired/canceled) */
export function isTerminalTicketStatus(status) {
  return TERMINAL_STATUSES.has(normalizeTicketStatus(status));
}

/**
 * Whether a ticket is live/open. Uses effective status so a just-expired OPEN
 * ticket (within the scheduler window) is not treated as open (audit F7).
 *
 * @param {object|null|undefined} ticket
 * @param {{ now?: number|Date }} [opts]
 * @returns {boolean}
 */
export function isOpenTicket(ticket, opts) {
  return effectiveTicketStatus(ticket, opts) === TicketStatus.OPEN;
}

/** Convenience inverse of {@link isOpenTicket} (terminal/inactive ticket). */
export function isInactiveTicket(ticket, opts) {
  const status = effectiveTicketStatus(ticket, opts);
  return status != null && status !== TicketStatus.OPEN;
}

/**
 * Presentation label for a status (display only). Unknown values fall back to a
 * neutral "Inactive" so the UI never renders a raw/illegal status string.
 *
 * @param {unknown} status
 * @returns {string}
 */
export function ticketStatusLabel(status) {
  const normalized = normalizeTicketStatus(status);
  return normalized ? STATUS_LABEL[normalized] : 'Inactive';
}
